import React, { useState, useEffect, useRef } from 'react';
import { Box, List, ListItemButton, ListItemText, Typography, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockClockIcon from '@mui/icons-material/LockClock';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EditIcon from '@mui/icons-material/Edit';
import IconTextChip from './IconTextChip';
import useOutsideClick from '../utils/useOutsideClick';
import {
  tabPanelBoxStyles,
  baseItemSx,
  deptItemSx,
  courseItemSx,
  profItemSx,
} from '../utils/courseTabStyles';

const AdminControlTabs = ({
  departments = [],
  courses = [],
  professors = [],
  onDepartmentSelect,
  onCourseSelect,
  onApprove,
  onReject,
  onEditDepartment,
  onEditCourse,
  onEditProfessorCourse,
  selectedDept: selectedDeptProp,
  selectedCourse: selectedCourseProp,
  selectedProfessor: selectedProfessorProp,
  highlightDept,
  highlightCourse,
  highlightProfessor,
}) => {
  const [internalDept, setInternalDept] = useState(departments[0]?.id || false);
  const [internalCourse, setInternalCourse] = useState(false);
  const [internalProfessor, setInternalProfessor] = useState(false);

  const isDeptControlled = selectedDeptProp !== undefined;
  const isCourseControlled = selectedCourseProp !== undefined;
  const isProfControlled = selectedProfessorProp !== undefined;

  const containerRef = useRef(null);

  const handleContainerClick = (e) => {
    if (!e.target.closest('.MuiListItemButton-root')) {
      if (isDeptControlled) {
        const dept = departments[0];
        if (dept && onDepartmentSelect) onDepartmentSelect(dept);
      } else if (internalDept) {
        setInternalDept(false);
        if (onDepartmentSelect) onDepartmentSelect(false);
      }

      if (isCourseControlled) {
        if (onCourseSelect) onCourseSelect({ id: '' });
      } else if (internalCourse) {
        setInternalCourse(false);
        if (onCourseSelect) onCourseSelect(false);
      }

      if (!isProfControlled && internalProfessor) {
        setInternalProfessor(false);
      }
    }
  };

  useOutsideClick(containerRef, () => {
    if (isDeptControlled) {
      const dept = departments[0];
      if (dept && onDepartmentSelect) onDepartmentSelect(dept);
    } else {
      const newVal = departments[0]?.id || false;
      setInternalDept(newVal);
      const dept = departments.find((d) => d.id === newVal);
      if (dept && onDepartmentSelect) onDepartmentSelect(dept);
    }

    if (isCourseControlled) {
      if (onCourseSelect) onCourseSelect({ id: '' });
    } else if (internalCourse) {
      setInternalCourse(false);
      if (onCourseSelect) onCourseSelect(false);
    }

    if (!isProfControlled && internalProfessor) {
      setInternalProfessor(false);
    }
  });

  const selectedDept = isDeptControlled ? selectedDeptProp : internalDept;
  const selectedCourse = isCourseControlled ? selectedCourseProp : internalCourse;
  const selectedProfessor = isProfControlled
    ? selectedProfessorProp
    : internalProfessor;

  useEffect(() => {
    if (!departments.some((d) => d.id === selectedDept)) {
      const newVal = departments[0]?.id || false;
      if (!isDeptControlled) setInternalDept(newVal);
      const dept = departments.find((d) => d.id === newVal);
      if (dept && onDepartmentSelect) onDepartmentSelect(dept);
    }
  }, [departments, selectedDept, isDeptControlled, onDepartmentSelect]);

  useEffect(() => {
    if (!courses.some((c) => c.id === selectedCourse)) {
      if (!isCourseControlled) {
        setInternalCourse(false);
        if (onCourseSelect) onCourseSelect(false);
      } else if (onCourseSelect) {
        onCourseSelect({ id: '' });
      }
    }
  }, [courses, selectedCourse, isCourseControlled, onCourseSelect]);

  useEffect(() => {
    if (!professors.some((p) => p.id === selectedProfessor)) {
      if (!isProfControlled) setInternalProfessor(false);
    }
  }, [professors, selectedProfessor, isProfControlled]);

  const handleDeptChange = (e, val) => {
    if (!isDeptControlled) setInternalDept(val);
    const dept = departments.find((d) => d.id === val);
    if (dept && onDepartmentSelect) onDepartmentSelect(dept);
  };

  const handleCourseChange = (e, val) => {
    if (!isCourseControlled) setInternalCourse(val);
    const course = courses.find((c) => c.id === val);
    if (course && onCourseSelect) onCourseSelect(course);
  };

  const handleProfessorChange = (e, val) => {
    if (!isProfControlled) setInternalProfessor(val);
  };

  const openEditDepartment = (dept) => {
    if (onEditDepartment) onEditDepartment(dept);
  };

  const openEditCourse = (course) => {
    if (onEditCourse) onEditCourse(course);
  };

  const openEditProfCourse = (prof) => {
    const course = courses.find((c) => c.id === selectedCourse);
    let professor = prof.professor;
    let professorSubmission = prof.professor_submission;

    if (!professor && !professorSubmission) {
      if (prof.is_submission) {
        professorSubmission = { id: prof.id, name: prof.name };
      } else {
        professor = { id: prof.id, name: prof.name };
      }
    } else {
      if (professor && !professor.name) professor = { ...professor, name: prof.name };
      if (professorSubmission && !professorSubmission.name)
        professorSubmission = { ...professorSubmission, name: prof.name };
    }

    const link = {
      id: prof.link_id || prof.id,
      course,
      professor,
      professor_submission: professorSubmission,
      rejection_reason: prof.rejection_reason,
      previous_submission: prof.previous_submission,
    };
    if (onEditProfessorCourse) onEditProfessorCourse(link);
  };

  const getDeptPendingMessage = (course) => {
    const dept = departments.find((d) => d.id === course.department?.id);
    return dept && (dept.is_pending || dept.is_rejected)
      ? `${dept.name} department must be approved first`
      : null;
  };

  const getCoursePendingMessage = (prof, courseId) => {
    if (!courseId) return null;
    if (prof.has_unapproved_link && prof.unapproved_course_id === courseId) {
      const ci = (prof.courses_info || []).find((c) => c.id === courseId);
      const name =
        prof.unapproved_course_name || ci?.short_name || ci?.long_name;
      return name ? `${name} course must be approved first` : null;
    }
    const course = courses.find((c) => c.id === courseId);
    if (
      course &&
      (course.is_pending || course.is_rejected) &&
      (prof.courses_info || []).some((ci) => ci.id === courseId)
    ) {
      const ci = prof.courses_info.find((c) => c.id === courseId) || {};
      const name =
        course.short_name ||
        course.long_name ||
        ci.short_name ||
        ci.long_name;
      return name ? `${name} course must be approved first` : null;
    }
    return null;
  };

  return (
    <Box ref={containerRef} sx={{ display: 'contents' }} onClick={handleContainerClick}>
      <Box sx={tabPanelBoxStyles}>
        <Typography
          variant="h6"
          align="center"
          sx={{
            py: 1,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'info.main',
            color: 'primary.contrastText',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          Departments
        </Typography>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <List sx={{ p: 0 }}>
            {departments.map((d) => {
              const awaiting = d.is_rejected && !d.is_pending;
              return (
                <ListItemButton
                  key={d.id}
                  selected={selectedDept === d.id}
                  onClick={() => handleDeptChange(null, d.id)}
                  alignItems="flex-start"
                  className={
                    highlightDept === d.id ? 'flash-highlight' : undefined
                  }
                  sx={{
                    ...deptItemSx,
                    color: awaiting
                      ? 'info.main'
                      : d.is_pending
                      ? 'error.main'
                      : undefined,
                    '&.Mui-selected': {
                      color: awaiting
                        ? 'info.main'
                        : d.is_pending
                        ? 'error.main'
                        : undefined,
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          {awaiting && <LockClockIcon fontSize="inherit" />}
                          {d.name}
                        </Box>
                      }
                      sx={{ my: 0, textAlign: 'center' }}
                    />
                    {selectedDept === d.id && d.is_pending && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {(() => {
                          const msg = getDeptPendingMessage(d);
                          return (
                            <>
                            <Tooltip title={msg} disableHoverListener={!msg} describeChild>
                              <span>
                                <IconTextChip
                                  icon={<CheckIcon fontSize="small" />}
                                  label="Approve"
                                  color="success"
                                  onClick={() => {
                                    if (!msg && onApprove) onApprove('department', d.id);
                                  }}
                                  disabled={Boolean(msg)}
                                  aria-label={msg || undefined}
                                />
                              </span>
                            </Tooltip>
                            <Tooltip title={msg} disableHoverListener={!msg} describeChild>
                              <span>
                                <IconTextChip
                                  icon={<CloseIcon fontSize="small" />}
                                  label="Reject"
                                  color="error"
                                  onClick={() => {
                                    if (!msg && onReject) onReject('department', d.id);
                                  }}
                                  disabled={Boolean(msg)}
                                  aria-label={msg || undefined}
                                />
                              </span>
                            </Tooltip>
                            <IconTextChip
                              icon={<EditIcon fontSize="small" />}
                              label="Edit"
                              color="info"
                              onClick={() => openEditDepartment(d)}
                            />
                          </>
                        );
                      })()}
                    </Box>
                  )}
                  {selectedDept === d.id && awaiting && !d.is_pending && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                      <Tooltip
                        title={
                          d.rejection_reason
                            ? `Rejection Reason: ${d.rejection_reason}`
                            : ''
                        }
                        disableHoverListener={!d.rejection_reason}
                        describeChild
                      >
                        <span>
                          <IconTextChip
                            icon={<HourglassEmptyIcon fontSize="small" />}
                            label="Awaiting Resubmission"
                            disabled
                            sx={{ color: 'text.disabled' }}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Box>

      <Box sx={tabPanelBoxStyles}>
        <Typography
          variant="h6"
          align="center"
          sx={{
            py: 1,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'info.main',
            color: 'primary.contrastText',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          Courses
        </Typography>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {courses.length > 0 ? (
            <List sx={{ p: 0 }}>
              {courses.map((c) => {
                const awaiting = c.is_rejected && !c.is_pending;
                return (
                  <ListItemButton
                    key={c.id}
                    selected={selectedCourse === c.id}
                    onClick={() => handleCourseChange(null, c.id)}
                    alignItems="flex-start"
                    className={
                      highlightCourse === c.id ? 'flash-highlight' : undefined
                    }
                    sx={{
                      ...courseItemSx,
                      color: awaiting
                        ? 'info.main'
                        : c.is_pending
                        ? 'error.main'
                        : undefined,
                      '&.Mui-selected': {
                        color: awaiting
                          ? 'info.main'
                          : c.is_pending
                          ? 'error.main'
                          : undefined,
                      },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {awaiting && <LockClockIcon fontSize="inherit" />}
                            {c.short_name}
                          </Box>
                        }
                        secondary={c.long_name}
                        sx={{ my: 0, textAlign: 'center' }}
                      />
                      {selectedCourse === c.id && c.is_pending && (
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {(() => {
                              const msg = getDeptPendingMessage(c);
                              return (
                                <>
                              <Tooltip title={msg} disableHoverListener={!msg} describeChild>
                                <span>
                                  <IconTextChip
                                    icon={<CheckIcon fontSize="small" />}
                                    label="Approve"
                                    color="success"
                                    onClick={() => {
                                      if (!msg && onApprove) onApprove('course', c.id);
                                    }}
                                    disabled={Boolean(msg)}
                                    aria-label={msg || undefined}
                                  />
                                </span>
                              </Tooltip>
                              <Tooltip title={msg} disableHoverListener={!msg} describeChild>
                                <span>
                                  <IconTextChip
                                    icon={<CloseIcon fontSize="small" />}
                                    label="Reject"
                                    color="error"
                                    onClick={() => {
                                      if (!msg && onReject) onReject('course', c.id);
                                    }}
                                    disabled={Boolean(msg)}
                                    aria-label={msg || undefined}
                                  />
                                </span>
                              </Tooltip>
                              <IconTextChip
                                icon={<EditIcon fontSize="small" />}
                                label="Edit"
                                color="info"
                                onClick={() => openEditCourse(c)}
                              />
                            </>
                          );
                        })()}
                      </Box>
                      )}
                      {selectedCourse === c.id && awaiting && !c.is_pending && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                          <Tooltip
                            title={
                              c.rejection_reason
                                ? `Rejection Reason: ${c.rejection_reason}`
                                : ''
                            }
                            disableHoverListener={!c.rejection_reason}
                            describeChild
                          >
                            <span>
                              <IconTextChip
                                icon={<HourglassEmptyIcon fontSize="small" />}
                                label="Awaiting Resubmission"
                                disabled
                                sx={{ color: 'text.disabled' }}
                              />
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          ) : (
            <Typography align="center" sx={{ py: 2 }}>
              No courses available
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={tabPanelBoxStyles}>
        <Typography
          variant="h6"
          align="center"
          sx={{
            py: 1,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'info.main',
            color: 'primary.contrastText',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          Professors
        </Typography>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {professors.length > 0 ? (
            <List sx={{ p: 0 }}>
              {professors.map((p) => {
                const awaiting = p.is_rejected && !p.is_pending;
                return (
                  <ListItemButton
                    key={p.id}
                    selected={selectedProfessor === p.id}
                    onClick={() => handleProfessorChange(null, p.id)}
                    alignItems="flex-start"
                    className={
                      highlightProfessor === p.id ? 'flash-highlight' : undefined
                    }
                    sx={{
                      ...profItemSx,
                      color: awaiting
                        ? 'info.main'
                        : p.is_pending
                        ? 'error.main'
                        : undefined,
                      '&.Mui-selected': {
                        color: awaiting
                          ? 'info.main'
                          : p.is_pending
                          ? 'error.main'
                          : undefined,
                      },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {awaiting && <LockClockIcon fontSize="inherit" />}
                            {p.name}
                          </Box>
                        }
                        sx={{ my: 0, textAlign: 'center' }}
                      />
                      {selectedProfessor === p.id && p.is_pending && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {(() => {
                            const msg = getCoursePendingMessage(p, selectedCourse);
                          const course = courses.find((c) => c.id === selectedCourse);
                          const coursePending =
                            course &&
                            course.is_pending &&
                            (p.courses_info || []).some((ci) => ci.id === selectedCourse);
                          const hasUnapprovedCourse =
                            (p.has_unapproved_link &&
                              p.unapproved_course_id === selectedCourse) ||
                            coursePending;
                          let actionType = p.is_submission
                            ? 'professor-submission'
                            : 'professor';
                          let targetId = p.id;
                          if (hasUnapprovedCourse) {
                            actionType = 'professor-course';
                            targetId = `${p.id}-${selectedCourse}`;
                          }
                          return (
                            <>
                              <Tooltip title={msg} disableHoverListener={!msg} describeChild>
                                <span>
                                  <IconTextChip
                                    icon={<CheckIcon fontSize="small" />}
                                    label="Approve"
                                    color="success"
                                    onClick={() => {
                                      if (!msg && onApprove) onApprove(actionType, targetId, p);
                                    }}
                                    disabled={Boolean(msg)}
                                    aria-label={msg || undefined}
                                  />
                                </span>
                              </Tooltip>
                              <Tooltip title={msg} disableHoverListener={!msg} describeChild>
                                <span>
                                  <IconTextChip
                                    icon={<CloseIcon fontSize="small" />}
                                    label="Reject"
                                    color="error"
                                    onClick={() => {
                                      if (!msg && onReject) onReject(actionType, targetId, p);
                                    }}
                                    disabled={Boolean(msg)}
                                    aria-label={msg || undefined}
                                  />
                                </span>
                              </Tooltip>
                              <IconTextChip
                                icon={<EditIcon fontSize="small" />}
                                label="Edit"
                                color="info"
                                onClick={() => openEditProfCourse(p)}
                              />
                            </>
                          );
                        })()}
                      </Box>
                    )}
                    {selectedProfessor === p.id && awaiting && !p.is_pending && (
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                        <Tooltip
                          title={
                            p.rejection_reason
                              ? `Rejection Reason: ${p.rejection_reason}`
                              : ''
                          }
                          disableHoverListener={!p.rejection_reason}
                          describeChild
                        >
                          <span>
                            <IconTextChip
                              icon={<HourglassEmptyIcon fontSize="small" />}
                              label="Awaiting Resubmission"
                              disabled
                              sx={{ color: 'text.disabled' }}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </ListItemButton>
                );
              })}
            </List>
          ) : (
            <Typography align="center" sx={{ py: 2 }}>
              No professors available
            </Typography>
          )}
        </Box>
      </Box>

    </Box>
  );
};

export default AdminControlTabs;
